(() => {
  const articleProductCache = new Map();

  function formatArticleProductMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2
      }).format(Number(cents) / 100);
    } catch (error) {
      return '$' + (Number(cents) / 100).toFixed(2);
    }
  }

  function getArticleProduct(link) {
    const productUrl = new URL(link.href, window.location.origin);
    if (productUrl.origin !== window.location.origin || !productUrl.pathname.includes('/products/')) {
      return Promise.resolve(null);
    }

    const endpoint = new URL(productUrl.href);
    endpoint.search = '';
    endpoint.hash = '';
    endpoint.pathname = endpoint.pathname.replace(/\/$/, '') + '.js';

    if (!articleProductCache.has(endpoint.href)) {
      articleProductCache.set(endpoint.href, fetch(endpoint.href, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error('Product request failed');
        return response.json();
      }));
    }

    return articleProductCache.get(endpoint.href);
  }

  function createArticleProductLink(className, productUrl, label) {
    const link = document.createElement('a');
    link.className = className;
    link.href = productUrl;
    link.textContent = label;
    return link;
  }

  function createArticleProductAction(product, productUrl, variant, canQuickAdd) {
    const action = document.createElement(canQuickAdd ? 'product-form' : 'div');
    action.className = 'gb-article-product-card__action';

    if (!canQuickAdd) {
      const label = product.available ? 'Choose options' : 'View product';
      const link = createArticleProductLink(
        'btn btn--primary gb-article-product-card__button',
        productUrl,
        label
      );
      action.appendChild(link);
      return action;
    }

    action.dataset.variantId = String(variant.id);

    const error = document.createElement('div');
    error.className = 'alert bg-error-bg text-error-text js-form-error gb-article-product-card__error';
    error.role = 'alert';
    error.hidden = true;

    const form = document.createElement('form');
    form.className = 'js-product-form';
    form.method = 'post';
    form.action = window.theme?.routes?.cartAdd || '/cart/add';

    const variantInput = document.createElement('input');
    variantInput.type = 'hidden';
    variantInput.name = 'id';
    variantInput.value = String(variant.id);

    const quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';

    const button = document.createElement('button');
    button.className = 'btn btn--primary gb-article-product-card__button';
    button.type = 'submit';
    button.name = 'add';
    button.dataset.addToCartText = 'Add to cart';
    button.setAttribute('aria-haspopup', 'dialog');
    button.textContent = 'Add to cart';

    const status = document.createElement('span');
    status.className = 'visually-hidden';
    status.setAttribute('aria-live', 'polite');

    const announceSuccess = (event) => {
      if (String(event.detail?.variantId || '') !== String(variant.id)) return;
      status.textContent = product.title + ' added to your cart.';
    };
    action.addEventListener('on:cart:add', announceSuccess);
    action.addEventListener('on:line-item:change', announceSuccess);

    form.append(variantInput, quantityInput, button);
    action.append(error, form, status);

    // product-form.js is loaded before this enhancement on article pages. A
    // custom element created after registration runs its constructor before
    // these children exist, so initialize it once the complete form is ready.
    if (typeof action.init === 'function') action.init();
    return action;
  }

  async function enhanceArticleProductLink(link) {
    if (link.dataset.gbProductCardReady) return;
    link.dataset.gbProductCardReady = 'loading';

    try {
      const product = await getArticleProduct(link);
      if (!product || !link.isConnected || !Array.isArray(product.variants)) return;

      const productUrl = new URL(link.href, window.location.origin);
      const requestedVariantId = productUrl.searchParams.get('variant');
      const requestedVariant = requestedVariantId
        ? product.variants.find((variant) => String(variant.id) === requestedVariantId)
        : null;
      const variant = requestedVariant || product.variants[0];
      const canQuickAdd = Boolean(
        variant?.available && (product.variants.length === 1 || requestedVariant)
      );
      const displayPrice = variant?.price ?? product.price;
      const card = document.createElement('aside');
      card.className = 'gb-article-product-card';
      card.dataset.productId = String(product.id || '');
      card.setAttribute('aria-label', 'Recommended product: ' + product.title);

      if (product.featured_image) {
        const media = createArticleProductLink(
          'gb-article-product-card__media',
          productUrl.href,
          ''
        );
        const imageUrl = new URL(product.featured_image, window.location.href);
        imageUrl.searchParams.set('width', '360');

        const image = document.createElement('img');
        image.className = 'gb-article-product-card__image';
        image.src = imageUrl.href;
        image.alt = product.title;
        image.width = 272;
        image.height = 272;
        image.loading = 'lazy';
        image.decoding = 'async';
        media.setAttribute('aria-label', 'View ' + product.title);
        media.appendChild(image);
        card.appendChild(media);
      } else {
        card.classList.add('gb-article-product-card--no-image');
      }

      const content = document.createElement('div');
      content.className = 'gb-article-product-card__content';

      const title = createArticleProductLink(
        'gb-article-product-card__title',
        productUrl.href,
        product.title
      );

      const pricing = document.createElement('div');
      pricing.className = 'gb-article-product-card__pricing';
      const price = document.createElement('span');
      price.className = 'gb-article-product-card__price';
      price.textContent = (product.price_varies && !requestedVariant ? 'From ' : '')
        + formatArticleProductMoney(displayPrice);
      pricing.appendChild(price);

      const purchase = document.createElement('div');
      purchase.className = 'gb-article-product-card__purchase';
      purchase.append(
        pricing,
        createArticleProductAction(product, productUrl.href, variant, canQuickAdd)
      );

      content.append(title, purchase);
      card.appendChild(content);

      const paragraph = link.parentElement;
      const target = paragraph?.tagName === 'P'
        && paragraph.children.length === 1
        && paragraph.textContent.trim() === link.textContent.trim()
        ? paragraph
        : link;
      target.replaceWith(card);
    } catch (error) {
      link.dataset.gbProductCardReady = 'fallback';
    }
  }

  function initArticleProductCards(scope = document) {
    scope.querySelectorAll('a.gb-product-card:not([data-gb-product-card-ready])')
      .forEach((link) => enhanceArticleProductLink(link));
  }

  function optimizeReviewImages(scope = document) {
    scope.querySelectorAll('.resource-item > img[data-original]:not([data-gb-review-media-ready])').forEach((image) => {
      image.dataset.gbReviewMediaReady = 'true';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.alt = '';

      const item = image.closest('.resource-item');
      const rawWidth = Number.parseFloat(image.getAttribute('width'));
      const rawHeight = Number.parseFloat(image.getAttribute('height'));
      const width = Number.isFinite(rawWidth) && rawWidth > 0
        ? Math.round(rawWidth)
        : (Number.isFinite(rawHeight) && rawHeight > 0 ? Math.round(rawHeight) : 252);
      const suppliedRatio = Number.parseFloat(item?.getAttribute('proportion'));
      const ratio = Number.isFinite(suppliedRatio) && suppliedRatio > 0
        ? suppliedRatio
        : (Number.isFinite(rawWidth) && rawWidth > 0 && Number.isFinite(rawHeight) && rawHeight > 0
            ? rawWidth / rawHeight
            : 1);

      image.width = width;
      image.height = Math.max(1, Math.round(width / ratio));
      if (Number.isFinite(suppliedRatio) && suppliedRatio > 0) {
        image.style.aspectRatio = String(ratio);
      }
    });
  }

  function initCollectionDescriptions(scope = document) {
    scope.querySelectorAll('[data-gb-collection-description]:not([data-gb-ready])').forEach((source) => {
      const content = source.querySelector('[data-gb-collection-description-content]');
      const destination = document.querySelector('[data-gb-collection-details-destination]');
      if (!content || !destination) return;

      source.dataset.gbReady = 'true';
      const handle = source.dataset.gbCollectionHandle || '';
      const title = source.dataset.gbCollectionTitle || 'this collection';
      let contentRoot = content;

      // Shopify content can contain several single-child wrapper divs. Walk
      // through them until the actual paragraphs are reached so descriptions,
      // including Flower Seeds, do not remain fully expanded above the grid.
      if (handle !== 'individual-seed-packets') {
        while (
          contentRoot.children.length === 1 &&
          contentRoot.firstElementChild?.tagName === 'DIV'
        ) {
          contentRoot = contentRoot.firstElementChild;
        }
      }

      const elements = Array.from(contentRoot.children);
      if (elements.length < 2) return;

      let summaryElement;

      if (handle === 'individual-seed-packets' && elements[0].tagName === 'DIV') {
        summaryElement = elements[0];
        summaryElement.classList.add('gb-collection-promo');
        source.classList.add('gb-collection-description--promo');
      } else {
        summaryElement = elements.find((element) =>
          element.tagName === 'P' && element.textContent.trim().length > 0
        ) || elements[0];
      }

      const detailNodes = Array.from(contentRoot.childNodes).filter((node) => {
        if (node === summaryElement) return false;
        return node.nodeType !== Node.TEXT_NODE || node.textContent.trim().length > 0;
      });

      // Flower Seeds has an unusually long opening paragraph. Keep a concise
      // 45-word introduction above the grid and preserve the complete original
      // paragraph as the first item in the below-grid disclosure.
      if (handle === 'flower-seeds-for-planting' && summaryElement.tagName === 'P') {
        const summaryWords = summaryElement.textContent.trim().split(/\s+/);
        if (summaryWords.length > 45) {
          detailNodes.unshift(summaryElement.cloneNode(true));
          summaryElement.textContent = summaryWords.slice(0, 45).join(' ') + '…';
        }
      }

      if (!detailNodes.length) return;

      const legacyDetails = document.querySelector('[data-gb-collection-legacy-details]');
      const legacyContent = legacyDetails?.querySelector('.gb-long-copy-details__content');
      if (legacyDetails && legacyContent) {
        const legacySummary = legacyDetails.querySelector('summary');
        if (legacySummary) legacySummary.textContent = 'Learn more about ' + title;
        const currentDescription = document.createDocumentFragment();
        detailNodes.forEach((node) => currentDescription.appendChild(node));
        legacyContent.prepend(currentDescription);
        legacyDetails.dataset.gbMerged = 'true';
        return;
      }

      const details = document.createElement('details');
      details.className = 'gb-collection-details';
      const summary = document.createElement('summary');
      summary.textContent = 'Learn more about ' + title;
      const detailContent = document.createElement('div');
      detailContent.className = 'rte gb-collection-details__content';
      detailNodes.forEach((node) => detailContent.appendChild(node));
      details.append(summary, detailContent);
      destination.appendChild(details);
    });
  }

  function initArticleToc(scope = document) {
    scope.querySelectorAll('.gb-toc:not([data-gb-toc-ready])').forEach((toc) => {
      toc.dataset.gbTocReady = 'true';

      // Keep the expandable table of contents focused on the article's main
      // H2 sections. H3 and H4 headings remain in the article's semantic HTML
      // for readers, accessibility, anchor links, and search engines.
      toc.querySelectorAll(':scope > ul > li > ul').forEach((nestedList) => {
        nestedList.remove();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCollectionDescriptions();
      initArticleToc();
      initArticleProductCards();
      optimizeReviewImages();
    }, { once: true });
  } else {
    initCollectionDescriptions();
    initArticleToc();
    initArticleProductCards();
    optimizeReviewImages();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCollectionDescriptions(event.target);
    initArticleToc(event.target);
    initArticleProductCards(event.target);
    optimizeReviewImages(event.target);
  });

  const reviewMediaObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('.resource-item > img[data-original]')) optimizeReviewImages(node.parentElement || document);
        else optimizeReviewImages(node);
      });
    });
  });

  reviewMediaObserver.observe(document.body, { childList: true, subtree: true });
})();
